import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { QueryFunction } from 'react-query';
import { useQueries } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Box, Dialog, Divider, FormHelperText, Stack, Typography, useTheme } from '@mui/material';

import {
  FolderPermissionsDTO,
  getGetFolderPermissionsQueryKey,
  LocationUpdateDTO,
  TalentCreateDTO,
  TalentCreateDTOType,
  TalentValidationResponseReason,
  useGetFolderPermissionsHook,
  useGetOrganizationStructureHook,
  useValidateTalent
} from '@/api/generated';
import ColoredPersonPlusIcon from '@/assets/icons/colored-person-plus.svg?react';
import CommonDialogContent from '@/components/Dialog/CommonDialogContent';
import CommonDialogTitle from '@/components/Dialog/CommonDialogTitle';
import { buildMapStructure, buildRecursiveStructure } from '@/components/fields/Location/locationTreeUtils';
import { CommonFormFooter } from '@/components/Form/Form';
import { useSnackbar } from '@/hooks/useSnackbar';
import useTalentBasicInfoForm from '@/hooks/useTalentBasicInfoForm';
import { ROUTER_PATHS } from '@/root/linker';
import { TalentBasicInfoFields } from '@/views/TalentEditInfoModal/BasicInfo';
import { TalentBasicInfoViewType } from '@/views/TalentEditInfoModal/constants';

interface CreateTalentModalProps {
  onClose: () => void;
  defaultOrganizationSlug?: string;
  defaultLocationSlug?: string;
  defaultType?: TalentCreateDTOType;
}

const CreateTalentModal = ({
  onClose,
  defaultOrganizationSlug,
  defaultLocationSlug,
  defaultType
}: CreateTalentModalProps) => {
  const { t } = useTranslation(['validation', 'talents']);
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const getFolderPermissions = useGetFolderPermissionsHook();

  const { formMethods } = useTalentBasicInfoForm({
    defaultValues: {
      type: defaultType || TalentCreateDTOType.CANDIDATE,
      organizationSlug: defaultOrganizationSlug,
      locations: defaultLocationSlug ? [defaultLocationSlug] : []
    },
    viewType: TalentBasicInfoViewType.CREATE
  });

  const organizationSlug = formMethods.watch('organizationSlug');
  const locations = formMethods.watch('locations');

  const folderPermissionsQueries = useMemo(
    () =>
      locations.map((location: string) => {
        const queryKey = getGetFolderPermissionsQueryKey(organizationSlug!, location);
        const queryFn: QueryFunction<FolderPermissionsDTO> = ({ signal }) =>
          getFolderPermissions(organizationSlug!, location, signal);

        return {
          queryKey,
          queryFn,
          enabled: !!organizationSlug && !!location
        };
      }),
    [organizationSlug, locations, getFolderPermissions]
  );

  const folderPermissionsResults = useQueries(folderPermissionsQueries);

  const canCreateTalents = useMemo(
    () =>
      folderPermissionsResults.length > 0 &&
      folderPermissionsResults.every(result => result.data?.canCreateTalents ?? false),
    [folderPermissionsResults]
  );

  const canCreateTalentsLoading = useMemo(
    () => folderPermissionsResults.some(result => result.isLoading),
    [folderPermissionsResults]
  );

  const [validationError, setValidationError] = useState<string | null>(null);

  const { mutateAsync: validateTalent, isLoading: isValidating } = useValidateTalent();

  const getOrganizationStructure = useGetOrganizationStructureHook();

  const populateLocations = useCallback(
    async (state: TalentCreateDTO) => {
      const organizationStructure = await getOrganizationStructure(state.organizationSlug as string);
      const recursiveStructure = organizationStructure && buildRecursiveStructure(organizationStructure);
      const mapStructure = recursiveStructure && buildMapStructure(recursiveStructure);
      const originalLocations = state.locations as unknown as string[];
      const locations: LocationUpdateDTO[] = originalLocations
        .map(location => mapStructure[location]!)
        .map(location => ({
          slug: location.slug!,
          type: location.type!
        }));

      return {
        ...state,
        locations: locations
      };
    },
    [getOrganizationStructure]
  );

  const handleNextAction = useCallback(
    async (state: TalentCreateDTO) => {
      try {
        setValidationError(null);
        const populatedState = await populateLocations(state);
        const validationResult = await validateTalent({ data: populatedState });

        if (validationResult.canCreate) {
          navigate(ROUTER_PATHS.TALENTS_CREATE, { state });
        } else {
          let message = '';

          switch (validationResult.reason) {
            case TalentValidationResponseReason.ALREADY_EXISTS:
              message = t('validation:talent.alreadyExists');
              break;
            case TalentValidationResponseReason.HIDDEN_TALENT:
              message = t('validation:talent.hidden');
              break;
            case TalentValidationResponseReason.REQUIRED_FIELD_MISSING:
              message = t('validation:talent.requiredMissing');
              break;
            default:
              message = t('validation:alerts.failed');
          }

          setValidationError(message);
        }
      } catch (error) {
        showMessage(t('validation:alerts.failed'));
      }
    },
    [populateLocations, validateTalent, navigate, t, showMessage]
  );

  useEffect(() => {
    const subscription = formMethods.watch(() => {
      if (validationError) setValidationError(null);
    });

    return () => subscription.unsubscribe();
  }, [formMethods, validationError]);

  return (
    <FormProvider {...formMethods}>
      <Dialog open={true} onClose={onClose}>
        <CommonDialogTitle onClose={onClose}>
          <CreateTalentHeader />
        </CommonDialogTitle>
        <CommonDialogContent>
          <Box component="form" onSubmit={formMethods.handleSubmit(handleNextAction)}>
            <Divider />
            <TalentBasicInfoFields
              viewType={TalentBasicInfoViewType.CREATE}
              sx={{ p: 4 }}
              disabledFields={{
                type: !!defaultType,
                locations: !!defaultLocationSlug
              }}
            />

            {validationError && (
              <Box sx={{ px: 4, pb: 2, mt: -2 }}>
                <FormHelperText error sx={{ mx: 0, mb: 1 }}>
                  {validationError}
                </FormHelperText>
              </Box>
            )}

            {!canCreateTalents && !canCreateTalentsLoading && organizationSlug && locations.length > 0 && (
              <Box sx={{ px: 4, pb: 2, mt: -2 }}>
                <FormHelperText error sx={{ mx: 0, mb: 1 }}>
                  {t('talents:layouts.talentCreateModal.cannotCreateInLocation')}
                </FormHelperText>
              </Box>
            )}

            <CommonFormFooter
              showResetButton={false}
              submitText={t('common:actions.next')}
              buttonsContainerSx={{ justifyContent: 'flex-end' }}
              disableSubmit={isValidating || !!validationError || !canCreateTalents}
            />
          </Box>
        </CommonDialogContent>
      </Dialog>
    </FormProvider>
  );
};

export default CreateTalentModal;

const CreateTalentHeader = () => {
  const { t } = useTranslation();
  const { spacing, palette } = useTheme();

  return (
    <>
      <Stack direction="row" alignItems="center" gap={2}>
        <Box sx={{ padding: spacing(0.5), borderRadius: spacing(0.5), background: palette.green[100] }}>
          <ColoredPersonPlusIcon display="block" width={44} height={44} />
        </Box>

        <Typography component="h2" variant="h3">
          {t('talents:layouts.talentCreateModal.title')}
        </Typography>
      </Stack>
    </>
  );
};
